(function initializeListingUtils(globalObject) {
  'use strict';

  function toggleFilter(filters, filterName, filterValue) {
    const nextFilters = { ...filters };
    const activeValues = Array.isArray(nextFilters[filterName]) ? [...nextFilters[filterName]] : [];
    const isActive = activeValues.includes(filterValue);

    if (isActive) {
      const remainingValues = activeValues.filter((value) => value !== filterValue);
      if (remainingValues.length > 0) {
        nextFilters[filterName] = remainingValues;
      } else {
        delete nextFilters[filterName];
      }
    } else {
      nextFilters[filterName] = [...activeValues, filterValue];
    }

    return {
      filters: nextFilters,
      active: !isActive,
    };
  }

  function normalizeListingView(view) {
    return view === 'list' ? 'list' : 'grid';
  }

  function resolveListingView({ storedView, defaultView = 'grid', allowGrid = true, allowList = true } = {}) {
    if (!allowGrid) return 'list';
    if (!allowList) return 'grid';
    if (storedView === 'grid' || storedView === 'list') return storedView;
    return normalizeListingView(defaultView);
  }

  function buildListingUrl({ pathname, searchTerms = '', filters = {}, sortBy = '', page = 0, index = 'products' }) {
    const queryParts = [];

    if (searchTerms) {
      queryParts.push(`q=${encodeURIComponent(searchTerms)}`);
    }

    Object.keys(filters).forEach((filterName) => {
      const values = filters[filterName];
      if (!Array.isArray(values) || values.length === 0) return;

      if (filterName === 'price') {
        queryParts.push(`price=${encodeURIComponent(`${values[0]}:${values[1]}`)}`);
        return;
      }

      values.forEach((value) => {
        queryParts.push(`${encodeURIComponent(filterName)}=${encodeURIComponent(value)}`);
      });
    });

    if (sortBy) {
      queryParts.push(`sort_by=${encodeURIComponent(sortBy)}`);
    }

    if (Number(page) !== 0) {
      queryParts.push(`page=${encodeURIComponent(page)}`);
    }

    if (index && index !== 'products') {
      queryParts.push(`index=${encodeURIComponent(index)}`);
    }

    return queryParts.length > 0 ? `${pathname}?${queryParts.join('&')}` : pathname;
  }

  // Every sort option a published config offers, one per ACTIVE DIRECTION rather than one per key.
  //
  // The caller previously kept a single option per entry, so `desc` overwrote `asc` when both were
  // active while the value was always built with `sort.asc ? 'asc' : 'desc'`. An entry with both
  // directions therefore rendered one control labelled "Price: High to Low" whose value sorted low
  // to high. The D2C config activates one direction per key so it never surfaced; the B2B config
  // activates both for `title` and `price`.
  //
  // `indexName` carries the replica the option sorts on. The B2B config publishes it per direction,
  // which is more reliable than composing `<index><suffix>`: its "Newest" replica is suffixed
  // `published_at_desc` while the record field is `published_at_timestamp`, so a composed name
  // pointed at an index that does not exist.
  function sortOptionsFromConfig(sortData) {
    if (!Array.isArray(sortData)) return [];

    const options = [];
    sortData.forEach((sort) => {
      if (!sort || !sort.key) return;

      ['asc', 'desc'].forEach((direction) => {
        const option = sort[direction];
        if (!option || !option.active) return;

        options.push({
          key: sort.key,
          direction,
          value: `_${sort.key}_${direction}`,
          id: `Filter_${sort.key}_${direction}`,
          title: String(option.title || '')
            .toLowerCase()
            .replace(/\b\w/g, (character) => character.toUpperCase()),
          indexName: option.index_name || '',
        });
      });
    });

    return options;
  }

  // The "[start] - [end] out of [total]" range for a result page.
  //
  // `end` was `page * hitsPerPage + hitsPerPage`, i.e. always the full page size, so a page of 8 results
  // announced "1 - 40 out of 8 results found". It has to be clamped to the number of hits. Zero hits has no
  // first item, so the range starts at 0 rather than claiming a first result that is not there.
  function formatResultRange(template, { page = 0, hitsPerPage = 0, nbHits = 0 } = {}) {
    const total = Math.max(0, Number(nbHits) || 0);
    const size = Math.max(0, Number(hitsPerPage) || 0);
    const offset = Math.max(0, Number(page) || 0) * size;
    const start = total === 0 ? 0 : Math.min(offset + 1, total);
    const end = Math.min(offset + size, total);

    return String(template || '')
      .replace('[start]', start)
      .replace('[end]', end)
      .replace('[total]', total);
  }

  globalObject.ListingUtils = Object.freeze({
    formatResultRange,
    buildListingUrl,
    normalizeListingView,
    resolveListingView,
    sortOptionsFromConfig,
    toggleFilter,
  });
})(globalThis);
